import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const BASE_URL = "https://www.eurocollis.com";

/** ISO hreflang codes mapped to our URL prefixes */
const HREFLANG_MAP: Record<string, string> = {
  en: "",
  fr: "/fr",
  es: "/es",
  it: "/it",
  ar: "/ar",
  sv: "/se",
};

/** OG locale codes */
const OG_LOCALE: Record<string, string> = {
  en: "en_GB",
  fr: "fr_FR",
  es: "es_ES",
  it: "it_IT",
  ar: "ar_MA",
  se: "sv_SE",
};

interface BreadcrumbItem {
  name: string;
  path: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  jsonLd?: Record<string, any> | Record<string, any>[];
  noindex?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  keywords?: string;
  /** Override the page-level @type for rich snippets */
  pageType?: string;
}

/**
 * Per-page SEO component. Sets <title>, meta description, keywords,
 * canonical, Open Graph (with locale), Twitter cards, hreflang alternates,
 * geo meta, breadcrumb JSON-LD, and optional additional JSON-LD.
 */
export default function SEOHead({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage = `${BASE_URL}/og-image.jpg`,
  jsonLd,
  noindex = false,
  breadcrumbs,
  keywords,
}: SEOHeadProps) {
  const location = useLocation();
  const { i18n } = useTranslation();
  const currentLang = i18n.language || "en";

  const fullCanonical = canonical
    ? canonical.startsWith("http") ? canonical : `${BASE_URL}${canonical}`
    : undefined;

  // Strip language prefix from current path to get base path for hreflang
  const basePath = canonical || location.pathname.replace(/^\/(fr|es|it|ar|se)/, "") || "/";

  // OG locale
  const ogLocale = OG_LOCALE[currentLang] || OG_LOCALE.en;

  // Build breadcrumb JSON-LD
  const breadcrumbLd = breadcrumbs && breadcrumbs.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: breadcrumbs.map((item, i) => ({
          "@type": "ListItem",
          position: i + 1,
          name: item.name,
          item: `${BASE_URL}${item.path}`,
        })),
      }
    : null;

  // Normalize jsonLd to always be an array for @graph support
  const jsonLdArray = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet>
      <html lang={currentLang} dir={currentLang === "ar" ? "rtl" : "ltr"} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />}
      {fullCanonical && <link rel="canonical" href={fullCanonical} />}

      {/* Author & Publisher */}
      <meta name="author" content="EuroCollis" />
      <meta name="publisher" content="EuroCollis" />

      {/* Geo meta — Morocco-headquartered, serving Europe */}
      <meta name="geo.region" content="MA" />
      <meta name="geo.placename" content="Morocco" />

      {/* Hreflang alternates */}
      {Object.entries(HREFLANG_MAP).map(([lang, prefix]) => (
        <link
          key={lang}
          rel="alternate"
          hrefLang={lang}
          href={`${BASE_URL}${prefix}${basePath === "/" ? "/" : basePath}`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${BASE_URL}${basePath === "/" ? "/" : basePath}`} />

      {/* Open Graph — enhanced with locale */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="EuroCollis — Smart Parcel Delivery Between Europe and Africa" />
      <meta property="og:site_name" content="EuroCollis" />
      <meta property="og:locale" content={ogLocale} />
      {Object.entries(OG_LOCALE)
        .filter(([l]) => l !== currentLang)
        .map(([, locale]) => (
          <meta key={locale} property="og:locale:alternate" content={locale} />
        ))}
      {fullCanonical && <meta property="og:url" content={fullCanonical} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@eurocollis" />

      {/* Breadcrumb JSON-LD */}
      {breadcrumbLd && (
        <script type="application/ld+json">{JSON.stringify(breadcrumbLd)}</script>
      )}

      {/* Additional JSON-LD(s) */}
      {jsonLdArray.map((ld, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(ld)}</script>
      ))}
    </Helmet>
  );
}
